import React, { useEffect, useLayoutEffect, useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import cn from 'classnames';
import uuid from 'uuid/v4';
import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import isFunction from 'lodash/isFunction';
import debounce from 'lodash/debounce';
import { tree as d3Tree, hierarchy as d3Hierarchy } from 'd3-hierarchy';
import { select as d3Select } from 'd3-selection';
import { zoom as d3Zoom, zoomIdentity as d3ZoomIdentity, zoomTransform as d3ZoomTransform } from 'd3-zoom';

import * as BrowserHelper from '@Services/BrowserHelper';
import { LEAF_ADORNMENT_WIDTH } from '@Pages/RetroSynthesis/ProjectRoute/ProjectRouteTreeView/components/SubstanceLeafNodeAdornment/constants';
import {
  ORIENTATION_BOTTOM_TO_TOP,
  ORIENTATION_LEFT_TO_RIGHT,
  ORIENTATION_RIGHT_TO_LEFT,
  ORIENTATION_TOP_TO_BOTTOM,
} from '@Components/D3Tree/constants';

import { propTypes } from './propTypes';
import { isHorizontal, transformToOrientation } from '../utils';
import Node from '../Node';
import Link from '../Link';

import '../globalCss.scss';

export const rd3tSvgClassName = 'rd3t-svg';
export const rd3tGClassName = 'rd3t-g';

// This is the most tricky part
// If translate or zoom is changed via props rather than from d3Zoom callback -
// need to conform d3Zoom internal state accordingly to prevent jumps
const _conformD3ZoomInternalsDebounceDelay = BrowserHelper.detectIE() ? 750 : 350;
const _conformD3ZoomInternalsDebounced = debounce((translate, zoom, treeId) => {
  const svg = d3Select(`.${rd3tSvgClassName}[uuid="${treeId}"]`);
  const svgNode = svg.node();

  // Check if the SVG element exists before trying to get its zoom transform
  if (!svgNode) {
    return;
  }

  const zoomTransform = d3ZoomTransform(svgNode);

  if (zoom !== zoomTransform.k || translate.x !== zoomTransform.x || translate.y !== zoomTransform.y) {
    svg.call(d3Zoom()?.transform, d3ZoomIdentity.translate(translate.x, translate.y)?.scale(zoom));
  }
}, _conformD3ZoomInternalsDebounceDelay);

// Enrich data with __rd3t object
// NOTE: returns Array
function enrichTreeData(data) {
  const dataArr = isArray(data) ? data : [data];
  return dataArr.map(({ children, ...n }) => ({
    ...n,
    __rd3t: {
      id: uuid(),
    },
    ...(children && { children: enrichTreeData(children) }),
  }));
}

// Build d3 tree hierarchy
function buildTree({ data: rawData, orientation, nodeSize, depthFactor, separation }) {
  if (!rawData) {
    return { nodes: [], links: [] };
  }
  const data = enrichTreeData(rawData)?.[0];
  const tree = d3Tree()
    ?.nodeSize(isHorizontal(orientation) ? [nodeSize.y, nodeSize.x] : [nodeSize.x, nodeSize.y])
    ?.separation((a, b) => (a?.parent?.data?.__rd3t?.id === b?.parent?.data?.__rd3t?.id ? separation.siblings : separation.nonSiblings));
  const rootNode = tree(d3Hierarchy(data));
  const nodes = rootNode.descendants();
  const links = rootNode.links();

  if (depthFactor) {
    nodes.forEach((node) => {
      // eslint-disable-next-line no-param-reassign
      node.y = node.depth * depthFactor;
    });
  }

  return { nodes, links };
}

// eslint-disable-next-line react/display-name
const RsTree = forwardRef(
  (
    {
      className,
      style,
      // Tree props
      data,
      translate,
      zoom,
      zoomable,
      scaleExtent,
      depthFactor,
      orientation,
      separation,
      nodeSize,
      shouldResize,
      onUpdate,
      leafPadding = 0,
      // Link props
      pathFunc,
      pathClassFunc,
      onLinkClick,
      onLinkMouseOver,
      onLinkMouseOut,
      // Node props
      rootNodeClassName,
      branchNodeClassName,
      leafNodeClassName,
      rootNodeAriaLabel,
      branchNodeAriaLabel,
      leafNodeAriaLabel,
      renderCustomNodeElement,
      onNodeClick,
      onNodeMouseOver,
      onNodeMouseOut,
      onResizeLayout,
      addNodeEnabled,
      treeId = uuid(),
    },
    ref
  ) => {
    // Rebuild tree
    const { links, nodes } = useMemo(() => buildTree({ data, orientation, nodeSize, depthFactor, separation }), [data, orientation]);

    // Create containerRef explicitly, and set it to forwarded ref
    // pass nodes in ref
    const containerRef = useRef(null);
    useImperativeHandle(ref, () => ({
      nodes,
      containerSize: containerRef.current?.getBoundingClientRect(),
    }));

    const calculateNodeCoordsWithAdornment = (node) => {
      const coords = transformToOrientation(node, orientation);
      if (addNodeEnabled) {
        const isLeafNode = !!node?.data?._data?.leafNode;
        const adornmentSize = transformToOrientation({ x: 0, y: isLeafNode ? LEAF_ADORNMENT_WIDTH : 0 }, orientation);
        coords.x += adornmentSize.x;
        coords.y += adornmentSize.y;
      }
      return coords;
    };

    const padTree = (treeBoundaries) => {
      switch (orientation) {
        case ORIENTATION_TOP_TO_BOTTOM:
          return {
            ...treeBoundaries,
            yMax: treeBoundaries.yMax + leafPadding,
          };
        case ORIENTATION_BOTTOM_TO_TOP:
          return {
            ...treeBoundaries,
            yMin: treeBoundaries.yMin - leafPadding,
          };
        case ORIENTATION_LEFT_TO_RIGHT:
          return {
            ...treeBoundaries,
            xMax: treeBoundaries.xMax + leafPadding,
          };
        case ORIENTATION_RIGHT_TO_LEFT:
          return {
            ...treeBoundaries,
            xMin: treeBoundaries.xMin - leafPadding,
          };
        default:
          return treeBoundaries;
      }
    };

    const resizeLayout = () => {
      if (!containerRef.current || !isFunction(onResizeLayout)) {
        return;
      }

      const container = containerRef.current?.getBoundingClientRect();
      let treeBoundaries = { xMin: 0, yMin: 0, xMax: 0, yMax: 0 };

      // In the original tree origin point is target molecule,
      // so to get offset of the root node, we need to get min and max coordinates of the tree
      nodes.forEach((node) => {
        const { x, y } = calculateNodeCoordsWithAdornment(node);
        treeBoundaries = {
          xMin: Math.min(x, treeBoundaries.xMin),
          yMin: Math.min(y, treeBoundaries.yMin),
          xMax: Math.max(x, treeBoundaries.xMax),
          yMax: Math.max(y, treeBoundaries.yMax),
        };
      });

      treeBoundaries = padTree(treeBoundaries);

      onResizeLayout({ container, treeBoundaries });
    };

    useLayoutEffect(() => {
      resizeLayout();
    }, [orientation]);

    useLayoutEffect(() => {
      if (shouldResize) {
        resizeLayout();
      }
    }, [shouldResize]);

    // Zoom changed handler
    const onZoomHandler = (event) => {
      if (isFunction(onUpdate)) {
        onUpdate({
          zoom: event?.transform?.k,
          translate: { x: event?.transform?.x, y: event?.transform?.y },
        });
      }
    };

    // When translate or zoom change - conform d3Zoom internals via ref
    const _conformD3ZoomInternalsRef = useRef(_conformD3ZoomInternalsDebounced);
    useEffect(() => {
      // eslint-disable-next-line no-unused-expressions
      zoomable && _conformD3ZoomInternalsRef.current(translate, zoom, treeId);
    }, [translate, zoom]);

    // Zoomable

    useEffect(() => {
      if (zoomable) {
        const svg = d3Select(`.${rd3tSvgClassName}[uuid="${treeId}"]`);
        const svgNode = svg.node();

        // Check if the SVG element exists before setting up zoom
        if (!svgNode) {
          return;
        }

        const _d3Zoom = d3Zoom().scaleExtent([scaleExtent.min, scaleExtent.max]).on('zoom', onZoomHandler);
        svg.call(_d3Zoom);
      }
    }, [zoomable]);

    // Node specific class names
    function getNodeClassName(parent, nodeDatum) {
      const hasParent = !isNil(parent);
      const hasChildren = nodeDatum.children && nodeDatum.children.length > 0;

      if (!hasParent) {
        return rootNodeClassName;
      }
      if (hasChildren) {
        return branchNodeClassName;
      }
      return leafNodeClassName;
    }

    // Node specific aria-labels
    function getNodeAriaLabel(parent, nodeDatum) {
      const hasParent = !isNil(parent);
      const hasChildren = nodeDatum.children && nodeDatum.children.length > 0;

      if (!hasParent) {
        return rootNodeAriaLabel;
      }
      if (hasChildren) {
        return branchNodeAriaLabel;
      }
      return leafNodeAriaLabel;
    }

    return (
      <div className={cn('rd3t-tree-container', { 'rd3t-grabbable': zoomable }, className)} style={style}>
        {/* eslint-disable-next-line react/no-unknown-property */}
        <svg
          // eslint-disable-next-line react/no-unknown-property
          uuid={treeId}
          className={rd3tSvgClassName}
          width="100%"
          height="100%"
          ref={containerRef}
          aria-label={zoomable ? 'retrosynthesis-route-tree-view' : 'retrosynthesis-route-preview'}>
          <g className={rd3tGClassName} transform={`translate(${translate.x},${translate.y}) scale(${zoom})`}>
            {links.map((linkData, i) => (
              // eslint-disable-next-line jsx-a11y/anchor-is-valid
              <Link
                // eslint-disable-next-line react/no-array-index-key
                key={`link-${i}`}
                orientation={orientation}
                pathFunc={pathFunc}
                pathClassFunc={pathClassFunc}
                linkData={linkData}
                onClick={onLinkClick}
                onMouseOver={onLinkMouseOver}
                onMouseOut={onLinkMouseOut}
              />
            ))}
            {/* eslint-disable-next-line no-shadow */}
            {nodes.map(({ data, x, y, parent }, i) => (
              <Node
                key={`node-${data.id || i}`}
                data={data}
                position={{ x, y }}
                parent={parent}
                nodeClassName={getNodeClassName(parent, data)}
                nodeAriaLabel={getNodeAriaLabel(parent, data)}
                renderCustomNodeElement={renderCustomNodeElement}
                orientation={orientation}
                onNodeClick={onNodeClick}
                onNodeMouseOver={onNodeMouseOver}
                onNodeMouseOut={onNodeMouseOut}
              />
            ))}
          </g>
        </svg>
      </div>
    );
  }
);

RsTree.propTypes = propTypes;

export default RsTree;
